/**
 * Converts <image> tags in an SVG string into embedded Base64 data URLs.
 * Used to avoid CORS issues during development.
 */
async function inlineSvgImages(svgString: string): Promise<string> {
  // Create a DOM parser to manipulate the SVG
  const parser = new DOMParser()
  const svgDoc = parser.parseFromString(svgString, 'image/svg+xml')
  const images = svgDoc.querySelectorAll('image')

  for (const img of Array.from(images)) {
    const href = img.getAttribute('href') || img.getAttribute('xlink:href')
    if (!href || href.startsWith('data:')) continue

    try {
      const response = await fetch(href)
      const blob = await response.blob()

      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })

      img.setAttribute('href', dataUrl)
      img.removeAttribute('xlink:href')
    } catch (err) {
      console.warn(`Failed to inline image ${href}:`, err)
    }
  }

  return new XMLSerializer().serializeToString(svgDoc.documentElement)
}

/**
 * Render an SVG string onto a canvas and return both the rendered canvas
 * and a PNG data URL of the result.
 */
export async function svgToCanvas(
  canvas: HTMLCanvasElement,
  svgString: string,
  targetWidth?: number,
) {
  // ✅ In dev mode, inline images to avoid CORS issues
  if (import.meta.env.DEV) {
    svgString = await inlineSvgImages(svgString)
  }

  return new Promise<{ canvas: HTMLCanvasElement; dataUrl: string }>((resolve, reject) => {
    const blob = new Blob([svgString], { type: 'image/svg+xml' })
    const svgUrl = URL.createObjectURL(blob)

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = svgUrl

    img.onload = () => {
      // const canvas = document.createElement('canvas')
      // canvas.width = img.width
      // canvas.height = img.height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(svgUrl)
        reject(new Error('Unable to get canvas context'))
        return
      }

      ctx.drawImage(img, 0, 0)

      // Optional resize
      let output = canvas
      if (targetWidth) {
        const ratio = targetWidth / canvas.width
        const resized = document.createElement('canvas')
        resized.width = targetWidth
        resized.height = canvas.height * ratio
        resized.getContext('2d')?.drawImage(canvas, 0, 0, resized.width, resized.height)
        output = resized
      }

      const dataUrl = output.toDataURL('image/png', 0.9)
      URL.revokeObjectURL(svgUrl)
      resolve({ canvas: output, dataUrl })
    }

    img.onerror = (err) => {
      console.error('svgToCanvas img.onerror', err)
      URL.revokeObjectURL(svgUrl)
      reject(err)
    }
  })
}
